import { IPublicClientApplication } from "@azure/msal-browser";
import drop from "lodash/drop";
import isEqual from "lodash/isEqual";
import take from "lodash/take";

import PivotGridDataSource, {
    Field,
    Options,
} from "devextreme/ui/pivot_grid/data_source"; // Reference: https://github.com/DevExpress/DevExtreme/issues/17885
import DataSource, {
    Options as DataSourceOptions,
} from "devextreme/data/data_source"; // Reference: https://github.com/DevExpress/DevExtreme/issues/17885
import XmlaStore from "devextreme/ui/pivot_grid/xmla_store"; // Reference: https://github.com/DevExpress/DevExtreme/issues/17885
import CustomStore from "devextreme/data/custom_store";
import { LoadOptions } from "devextreme/data"; // Reference: https://github.com/DevExpress/DevExtreme/issues/17885
import ODataStore, {
    Options as ODataStoreOptions,
} from "devextreme/data/odata/store";
import { DateRange } from "@daypicker/react";

import { ISortOptions } from "../dx-interfaces";
import { ApiService, createDnspDashboardApiAsync } from "./api-service";
import {
    IDrEventStatistics,
    IDrEventInfo,
    IAddressSearchResponse,
    IGraphChargerMeterValueItem,
    convertTaskStageDtoToTaskStageViewModel,
    TaskStageViewModel,
} from "../view-models";
import {
    AlertDto,
    ChangeConfigurationTask,
    ConnectorUptimeSummary,
    DeviceConfigurationItemDto,
    DeviceConfigurationSchemaDto,
    DeviceDto,
    DeviceGroupDto,
    DeviceOwnerDto,
    DeviceSessionSummary,
    DeviceUptimeSummary,
    DnspGetDerEventResponse,
    GetChargeSessionCountIntervalEnum,
    GetDeviceConfigurationSchemaDeviceTypeEnum,
    OnlineStatusItem,
    OrgSessionSummary,
    OrgUptimeSummary,
    PayMateTransactionDto,
    PowerConsumption,
    PowerConsumptionResponse,
    SiteSessionSummary,
    SiteUptimeSummary,
    StatusItem,
    TaskInfoUpdateMessage,
    UserAccountDto,
    VehicleDto,
} from "@/api/openapi";
import AuthService from "./auth-service";
import { IErrorContextType } from "@/contexts/error";
import notify from "devextreme/ui/notify";
import {
    EXTERNAL_ORGANISATION_ID_HEADER,
    notifyDuration,
    notifyPosition,
} from "@/constants";
import { getExternalOrganisationId } from "@/utils/utils";
import { Transaction, ServiceEventDto } from "@/api/query.schemas";
import { HubConnection } from "@microsoft/signalr";

interface PowerConsumptionDataSource extends DataSource<
    PowerConsumption,
    string
> {
    response?: PowerConsumptionResponse[];
    timer?: NodeJS.Timeout;
}

const detailedTransactionColumns: (keyof IGraphChargerMeterValueItem)[] = [
    "chargerMeterValueId",
    "totalActiveEnergyImportWh",
    "totalActiveEnergyExportWh",
    "l1VoltageRmsV",
    "l2VoltageRmsV",
    "l3VoltageRmsV",
    "l1ActivePowerRmsW",
    "l2ActivePowerRmsW",
    "l3ActivePowerRmsW",
    "currentOfferedA",
    "powerOfferedW",
    "stateOfCharge",
    "timestamp",
];

/**
 * Provides data sources for use in devextreme components.
 * These datasources wrap API CRUD actions.
 *
 * @deprecated Do not use for new work. This service is slated for migration; prefer the newer data-fetching approach.
 */
class DataSourceService {
    public static readonly ProjectTotalName = "Project Total";
    /**
     * Set to true to have odata endpoints return data wrapped in a DevExtremeOdataResult object, suitable for the data grid
     */
    public static readonly OdataExtremeHeader = "x-odata-extreme";

    /**
     * Filter param to exclude connector statuses with count: 0.
     * TODO: move the data source into DevicesInfoComponent, make it a function component, make this a useState variable
     * once functionality is confirmed.
     */
    public filterEmptyConnectorStatus = false;

    /**
     * Update the data source service with a reference to the current SignalR hub
     * (This _could_ be done as a constructor param like msal, but,
     *  We don't need/want data source service to rebuild whenever signalR reconnects)
     */
    public signalRHub?: HubConnection;

    private _authHeader: string | undefined;
    private _errorContext: IErrorContextType;
    private _msalInstance: IPublicClientApplication;
    //private _addressDataSource: DataSource | undefined;
    //private _chargerMeterValuesDataSource: { urlSuffix: string, select: Array<string>, source: DataSource } | undefined;
    private _drEventInfoDataSource: DataSource | undefined;
    private _drEventStatsDataSource: DataSource | undefined;
    private _devicesODataSource: DataSource | undefined;
    private _deviceOwnersODataSource:
        | { select: Array<string>; source: DataSource }
        | undefined;
    private _devicesInfoDataSource: PivotGridDataSource | undefined;
    private _dnspDrEventStatsDataSources: { [dnspId: string]: DataSource };
    private _transactionsDataSource:
        | { urlSuffix: string; select: Array<string>; source: DataSource }
        | undefined;
    private _paymateSessionsDataSource:
        | { urlSuffix: string; select: Array<string>; source: DataSource }
        | undefined;
    private _usersDataSource:
        | { select: Array<string>; source: DataSource }
        | undefined;
    private _rfidTagsDataSource:
        | { select: Array<string>; source: DataSource }
        | undefined;
    private _deviceGroupDataSource:
        | { select: Array<string>; source: DataSource }
        | undefined;
    //private _taskStore: { [taskInfoId: string]: IStoredTask }
    //private _taskCallbacks: { [taskInfoId: string]: (task: IStoredTask) => void }

    private _deviceConfigurationDataSources: {
        [deviceIdentity_archiveId_schemaOnly: string]: DataSource;
    };

    /**
     * Creates a new DataSourceService instances.
     * @param errorContext The error context.
     * @param msalInstance The MSAL instance.
     */
    public constructor(
        errorContext: IErrorContextType,
        instance: IPublicClientApplication,
    ) {
        this._errorContext = errorContext;
        this._msalInstance = instance;
        this._dnspDrEventStatsDataSources = {};
        this._deviceConfigurationDataSources = {};
    }

    public deviceConfigurationStore(
        device: DeviceDto,
        dataSourceSchemaOnly: boolean = false,
    ): DataSource {
        const key = `${device.deviceIdentity}_${device.deviceArchiveId}_${dataSourceSchemaOnly}`;

        if (this._deviceConfigurationDataSources[key])
            return this._deviceConfigurationDataSources[key];
        const configItems = async () => {
            const api = await ApiService.createLookupApiAsync(
                this._errorContext,
                this._msalInstance,
            );
            //Fetch the available config schema for this device type and model, then add it to a `schema` field on DeviceConfigurationItem
            const schema = await api.getDeviceConfigurationSchema({
                deviceType:
                    device.deviceType as GetDeviceConfigurationSchemaDeviceTypeEnum,
                model: device.model,
            });
            const schemaLookup = schema.reduce(
                (m, o) => m.set(o.key!, Object.assign(m.get(o.key!) || {}, o)),
                new Map<string, DeviceConfigurationSchemaDto>(),
            );
            device.configurationItems = device.configurationItems ?? [];
            if (!dataSourceSchemaOnly) {
                const nextConfig: (DeviceConfigurationItemDto & {
                    schema?: DeviceConfigurationSchemaDto;
                })[] = device.configurationItems.map((c) => ({
                    ...c,
                    schema: schemaLookup.get(c.key!),
                }));
                return nextConfig;
            }
            const result = schema.map((schemaItem) => {
                let exConfigItem = device.configurationItems?.find(
                    (c) => c.key === schemaItem.key,
                );
                if (!exConfigItem) {
                    exConfigItem = {
                        key: schemaItem.key,
                        valueInProgress: "",
                        valueLastApplied: "",
                        isReadOnly: false,
                    };
                    device.configurationItems?.push(exConfigItem);
                }
                return {
                    ...exConfigItem,
                    schema: schemaItem,
                };
            });

            return result;
        };

        const store = new CustomStore({
            key: "key",
            load: configItems,
            insert: async (values) => {
                const newValue = values.valueInProgress;
                const newItem: DeviceConfigurationItemDto = {
                    key: values.key,
                    valueInProgress: newValue,
                    status: "Applying",
                    isReadOnly: false,
                };
                const api = await ApiService.createDeviceApiAsync(
                    this._errorContext,
                    this._msalInstance,
                );
                const response = await api.deviceConfigurationUpdate({
                    deviceIdentity: device.deviceIdentity,
                    body: {
                        key: newItem.key,
                        value: newItem.valueInProgress,
                    },
                });
                const taskId = response.identityStr;
                if (!taskId) {
                    // Done. Device was offline, no Task.
                    // The valueLastApplied field will only be updated when device next comes online.
                    newItem.valueInProgress = newValue;
                    newItem.status = "Applying";
                    newItem.updatedAt = new Date();
                    return newItem;
                }
                const successTask = await this.waitForTaskAsync(taskId); // Throws on Timeout
                console.log(
                    `data-source config insert resulted. Result: ${successTask.result}`,
                    successTask,
                );
                const stage = convertTaskStageDtoToTaskStageViewModel(
                    successTask.stage,
                );
                if (stage !== TaskStageViewModel.Complete) {
                    throw new Error(
                        successTask.result ?? "Error inserting config item",
                    );
                }
                // Success!
                newItem.valueLastApplied = newValue;
                newItem.valueInProgress = undefined;
                newItem.status = "Applied";
                newItem.updatedAt = new Date();
                return newItem;
            },
            update: async (key, values) => {
                const newValue = values.valueInProgress;
                const existingRecord = device.configurationItems!.find(
                    (c) => c.key === key,
                );
                const postBody: ChangeConfigurationTask = {
                    key: key,
                    value: newValue,
                };
                const api = await ApiService.createDeviceApiAsync(
                    this._errorContext,
                    this._msalInstance,
                );
                const response = await api.deviceConfigurationUpdate({
                    deviceIdentity: device.deviceIdentity,
                    body: postBody,
                });

                const taskId = response.identityStr;
                if (!taskId && existingRecord) {
                    // Done. Device was offline, no Task.
                    //The valueLastApplied field will only be updated when device next comes online.
                    existingRecord.valueInProgress = newValue;
                    existingRecord.status = "Applying";
                    existingRecord.updatedAt = new Date();
                    return existingRecord;
                } else if (!taskId) {
                    return existingRecord;
                }

                const successTask = await this.waitForTaskAsync(taskId); // Throws on Timeout
                console.log(
                    `data-source config update resulted. Result: ${successTask.result}`,
                    successTask,
                );
                const stage = convertTaskStageDtoToTaskStageViewModel(
                    successTask.stage,
                );
                if (stage !== TaskStageViewModel.Complete) {
                    throw new Error(
                        successTask.result ?? "Error updating config item",
                    );
                }
                // Success!
                notify(
                    {
                        message: `${key}: ${successTask.result ?? "applied"}`,
                        position: notifyPosition,
                    },
                    "success",
                    notifyDuration,
                );
                if (existingRecord) {
                    existingRecord.valueLastApplied = newValue;
                    existingRecord.valueInProgress = undefined;
                    existingRecord.status = "Applied";
                    existingRecord.updatedAt = new Date();
                }
                return existingRecord;
            },
        });
        const result = new DataSource(store);
        this._deviceConfigurationDataSources[key] = result;
        return result;
    }

    public get drEventInfoDataSource(): DataSource {
        if (this._drEventInfoDataSource) {
            return this._drEventInfoDataSource;
        }

        const store: CustomStore = new CustomStore({
            key: "drEventId",
            cacheRawData: false,
            loadMode: "raw",
            load: async (loadOptions: LoadOptions) => {
                const api = await createDnspDashboardApiAsync(
                    this._errorContext,
                    this._msalInstance,
                );
                let response: DnspGetDerEventResponse[] = [];
                let donePaging = false;
                const nowUtc: number = Date.now(); // Milliseconds since UTC epoch
                const epoch = Date.UTC(0, 0);
                const pageSize = 50;
                let cursor: number | undefined;

                while (!donePaging) {
                    response = await api.dnspGetDerEvents({
                        limit: pageSize,
                        startingBefore: cursor ? new Date(cursor) : undefined,
                        includeStatistics: true,
                    });

                    if (response.length > 0) {
                        const lastItem = response[response.length - 1];
                        cursor = lastItem.startUtc
                            ? lastItem.startUtc.getTime()
                            : epoch;
                        donePaging =
                            response.length < pageSize ||
                            (response.length > 0 && cursor < nowUtc);
                    } else {
                        donePaging = true;
                    }
                }

                const data: IDrEventInfo[] = response
                    .flatMap((i) => {
                        const optOuts = i.optOutCount ?? 0;
                        const optIns = (i.participantCount ?? 0) - optOuts;
                        const viewModel: IDrEventInfo = {
                            drEventId: i.derEventId ?? "",
                            dnsp: i.dnspName ?? "",
                            optIns: optIns,
                            optOuts: optOuts,
                            start: i.startUtc || new Date(),
                            end: i.endUtc || new Date(),
                        };

                        return viewModel;
                    })
                    .filter((i) => {
                        return i.start.getTime() >= nowUtc;
                    });

                return DataSourceService.applyLoadOptions(loadOptions, data);
            },
        });

        this._drEventInfoDataSource = DataSourceService.createDataSource(
            store,
            undefined,
            false,
        );

        return this._drEventInfoDataSource;
    }

    public get drEventStatsDataSource(): DataSource {
        if (this._drEventStatsDataSource) {
            return this._drEventStatsDataSource;
        }

        const store: CustomStore = new CustomStore({
            key: "drEventId",
            loadMode: "raw",
            cacheRawData: true,
            load: async (loadOptions: LoadOptions) => {
                const api = await createDnspDashboardApiAsync(
                    this._errorContext,
                    this._msalInstance,
                );
                const response = await api.dnspGetDerEvents({
                    limit: 10000,
                    includeStatistics: true,
                    startingBefore: new Date(), // Only get historic DR events.
                });
                const data: IDrEventStatistics[] = response.map((i) => {
                    const viewModel: IDrEventStatistics = {
                        drEventId: i.derEventId || "",
                        dnsp: i.dnspName || "",
                        kWhSaved: i.powerSavedWh || 0,
                        participants: i.participantCount || 0,
                        chargers: i.deviceCount || 0,
                        start: i.startUtc || new Date(),
                        end: i.endUtc || new Date(),
                    };

                    return viewModel;
                });

                return DataSourceService.applyLoadOptions(loadOptions, data);
            },
        });

        this._drEventStatsDataSource = DataSourceService.createDataSource(
            store,
            5,
            true,
        );

        return this._drEventStatsDataSource;
    }

    public addressLookupDataSource(searchParams: string[][]): DataSource {
        const url = new URL("https://atlas.microsoft.com/search/address/json");

        const store: CustomStore = new CustomStore({
            key: "id",
            loadMode: "processed",
            cacheRawData: false,
            load: async (options) => {
                console.log("load options: ", options);
                if (options.searchExpr) {
                    url.search = new URLSearchParams(
                        [[`query`, `${options.searchValue}`]].concat(
                            searchParams,
                        ),
                    ).toString();
                    console.log("url search: ", url.search);
                    const response = await fetch(url.toString())
                        .then((response) => response.json())
                        .then((data: IAddressSearchResponse) => data);
                    console.log("address search response: ", response);
                    return response.results;
                } else {
                    throw Error("Could Not Retrieve Load Options");
                }
            },
        });

        return DataSourceService.createDataSource(store);
    }

    public async getStatusLogsODataSource(
        deviceIdentity: string,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<StatusItem>> {
        const urlSuffix = `/devices/${deviceIdentity}/status-logs-odata`;
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getUniqueDevicesODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource> {
        const urlSuffix = "/devices-odata";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async devicesODataSourceAsync(): Promise<DataSource> {
        if (!this._devicesODataSource) {
            const newDataSource = await this.getUniqueDevicesODataSource();
            this._devicesODataSource = newDataSource;
        }

        return this._devicesODataSource;
    }

    public async getUniqueDeviceGroupsODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<DeviceGroupDto, string>> {
        const urlSuffix = "/device-groups-odata";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getServiceEventsODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<ServiceEventDto, number>> {
        const urlSuffix = "/service-events-odata";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "serviceEventId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async transactionsDataSourceAsync(
        deviceIdentity: string | undefined,
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<Transaction>> {
        const urlSuffix = deviceIdentity
            ? `/devices/${deviceIdentity}/transactions-odata`
            : `/transactions/transactions-odata`;

        if (
            !!this._transactionsDataSource &&
            this._transactionsDataSource.urlSuffix === urlSuffix &&
            isEqual(this._transactionsDataSource.select, select) &&
            isEqual(
                this._transactionsDataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        ) {
            return this._transactionsDataSource.source;
        }
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "transactionId",
            select,
            undefined,
            dataSourceOptions,
        );

        this._transactionsDataSource = {
            urlSuffix: urlSuffix,
            select: select,
            source: dataSource,
        };
        return dataSource;
    }

    public async paymateSessionsDataSourceAsync(
        deviceIdentity: string | undefined,
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<PayMateTransactionDto>> {
        const urlSuffix = `/devices/${deviceIdentity}/paymate-sessions`;

        if (
            !!this._paymateSessionsDataSource &&
            this._paymateSessionsDataSource.urlSuffix === urlSuffix &&
            isEqual(this._paymateSessionsDataSource.select, select) &&
            isEqual(
                this._paymateSessionsDataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        ) {
            return this._paymateSessionsDataSource.source;
        }
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "payMateSessionId",
            select,
        );

        this._paymateSessionsDataSource = {
            urlSuffix: urlSuffix,
            select: select,
            source: dataSource,
        };
        return dataSource;
    }

    public async getTransactionChargerMeterValuesODataSource(
        transactionId: number,
        select: (keyof IGraphChargerMeterValueItem)[] = detailedTransactionColumns,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<IGraphChargerMeterValueItem>> {
        const urlSuffix = `/transactions/${transactionId}/charger-meter-values-odata`;
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "chargerMeterValueId",
            select,
            undefined,
            {
                sort: [{ selector: "timestamp", desc: false }],
                ...dataSourceOptions,
            },
        );
        return dataSource as DataSource<IGraphChargerMeterValueItem>;
    }

    public async deviceOwnersDataSourceAsync(
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<DeviceOwnerDto>> {
        const urlSuffix = "/deviceowners-odata";

        if (
            !!this._deviceOwnersODataSource &&
            isEqual(this._deviceOwnersODataSource.select, select) &&
            isEqual(
                this._deviceOwnersODataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        )
            return this._deviceOwnersODataSource.source;

        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceOwnerId",
            select,
        );
        this._deviceOwnersODataSource = { select: select, source: dataSource };

        return dataSource;
    }

    public async usersDataSourceAsync(
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<UserAccountDto>> {
        const urlSuffix = "/user-accounts-odata";

        if (
            !!this._usersDataSource &&
            isEqual(this._usersDataSource.select, select) &&
            isEqual(
                this._usersDataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        ) {
            return this._usersDataSource.source;
        }

        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "userAccountId",
            select,
            undefined,
            dataSourceOptions,
        );
        this._usersDataSource = { select: select, source: dataSource };
        return dataSource;
    }

    public async rfidTagsDataSourceAsync(
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource> {
        const urlSuffix = "/rfidtags-odata";
        if (
            !!this._rfidTagsDataSource &&
            isEqual(this._rfidTagsDataSource.select, select) &&
            isEqual(
                this._rfidTagsDataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        ) {
            return this._rfidTagsDataSource.source;
        }

        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "rfidTagId",
            select,
            {
                keyType: "String",
                // Force `id` to String so ODataStore doesn't replace GUID-shaped values with a Guid instance.
                fieldTypes: { id: "String", label: "String" },
            },
            {},
        );
        this._rfidTagsDataSource = { select: select, source: dataSource };
        return dataSource;
    }

    public async deviceGroupDataSourceAsync(
        select: Array<string>,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<DeviceGroupDto>> {
        const urlSuffix = "/device-groups-odata";

        if (
            !!this._deviceGroupDataSource &&
            isEqual(this._deviceGroupDataSource.select, select) &&
            isEqual(
                this._deviceGroupDataSource.source.filter(),
                dataSourceOptions?.filter,
            )
        ) {
            return this._deviceGroupDataSource.source;
        }

        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            select,
            undefined,
            dataSourceOptions,
        );
        this._deviceGroupDataSource = { select: select, source: dataSource };
        return dataSource;
    }

    public async getUptimeDevicesODataSource(
        dataSourceOptions?: DataSourceOptions,
    ) {
        const urlSuffix = "/uptime-devices-odata";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getOnlineStatusLogsODataSource(
        deviceIdentity: string,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<OnlineStatusItem>> {
        const urlSuffix = `/devices/${deviceIdentity}/online-status-logs-odata`;
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getAlertsODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<AlertDto>> {
        const urlSuffix = "/alerts-odata";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "alertId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getOrganisationNotificationPreferencesODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource> {
        const urlSuffix = "/odata/organisation-notification-preferences";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "organisationNotificationPreferenceId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getAlertImpactedTransactionsODataSource(
        alertId: number,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<Transaction>> {
        const urlSuffix = `/alerts/${alertId}/impacted-transactions-odata`;
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "alertId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getNotificationPreferencesODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource> {
        const urlSuffix = "/odata/notification-preferences";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "notificationPreferenceId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getVehiclesODataSource(
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<VehicleDto>> {
        const urlSuffix = "/vehicles";
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "vehicleId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getCoreConfigHistoryODataSource(
        deviceIdentity: string,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource> {
        const urlSuffix = `/core/${deviceIdentity}/config/history`;
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "coreConfigurationId",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    /**
     * Helper function to append DateRange query parameters to a URL
     * @param url The base URL
     * @param dateRange Optional DateRange object with from and to dates
     * @returns URL with appended query parameters if dateRange is provided
     */
    private static appendDateRangeParams(
        url: string,
        dateRange?: DateRange,
    ): string {
        if (!dateRange) return url;

        const params = new URLSearchParams();
        if (dateRange.from) {
            params.append("from", dateRange.from.toISOString());
        }
        if (dateRange.to) {
            params.append("to", dateRange.to.toISOString());
        }

        const paramString = params.toString();
        return paramString ? `${url}?${paramString}` : url;
    }

    public async getGroupedSessionsByOrgODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<OrgSessionSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/charging-success-rate/sessions/grouped/by-org",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedSessionsBySiteODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<SiteSessionSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/charging-success-rate/sessions/grouped/by-site",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedSessionsByDeviceODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<DeviceSessionSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/charging-success-rate/sessions/grouped/by-device",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedUptimeByOrgODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<OrgUptimeSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/uptime/grouped/by-org",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedUptimeBySiteODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<SiteUptimeSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/uptime/grouped/by-site",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceGroupTag",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedUptimeByDeviceODataSource(
        dateRange?: DateRange,
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<DeviceUptimeSummary>> {
        const urlSuffix = DataSourceService.appendDateRangeParams(
            "/uptime/grouped/by-device",
            dateRange,
        );
        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    public async getGroupedUptimeByConnectorODataSource(
        dateRange?: DateRange,
        deviceGroupTags?: string[],
        dataSourceOptions?: DataSourceOptions,
    ): Promise<DataSource<ConnectorUptimeSummary>> {
        const params = new URLSearchParams();
        if (dateRange?.from)
            params.append("from", dateRange.from.toISOString());
        if (dateRange?.to) params.append("to", dateRange.to.toISOString());
        if (deviceGroupTags?.length)
            params.append("deviceGroupTags", deviceGroupTags.join(","));
        const paramString = params.toString();
        const urlSuffix = paramString
            ? `/uptime/grouped/by-connector?${paramString}`
            : "/uptime/grouped/by-connector";

        const dataSource = await this.buildODataSourceAsync(
            urlSuffix,
            "deviceIdentity",
            [],
            undefined,
            dataSourceOptions,
        );
        return dataSource;
    }

    /**
     * Build an OData DataSource. Common functionality to set url, auth and extreme headers, select list, etc.
     * Functionality can be extended or overriden by dataStoreOptions and dataSourceOptions.
     * @param urlSuffix including slash, eg "/rfidtags-odata"
     * @param key primary key of the OData items
     * @param select list of fields to select
     * @param dataStoreOptions optional. Object is merged with a default Options that sets the url,key,version, and intercepts ID lookups (replaceODataIDLookup) and PATCH/POST calls.
     * @param dataSourceOptions optional. Object is merged with a default Options that sets the select and dataSource.
     * @param odataExtremeHeader optional. Defaults to true.
     * @returns DataSource
     */
    public async buildODataSourceAsync(
        urlSuffix: string,
        key: string,
        select: Array<string>,
        dataStoreOptions?: ODataStoreOptions,
        dataSourceOptions?: DataSourceOptions,
        odataExtremeHeader: boolean = true,
    ): Promise<DataSource> {
        await this.getAuthHeaderAsync();
        const basePath: string = (
            await ApiService.getApiConfigAsync(this._msalInstance)
        ).basePath;
        const storeUrl = basePath + urlSuffix;

        const defaultDataStoreOptions: ODataStoreOptions = {
            url: storeUrl,
            key: key,
            deserializeDates: false,
            beforeSend: (options) => {
                this.getAuthHeaderAsync();

                options.headers.Authorization = this._authHeader;
                options.headers[DataSourceService.OdataExtremeHeader] =
                    odataExtremeHeader;
                options.headers[EXTERNAL_ORGANISATION_ID_HEADER] =
                    getExternalOrganisationId();

                if (options.method === "GET" || options.method === "get") {
                    options.url = DataSourceService.replaceODataIDLookup(
                        storeUrl,
                        key,
                        options.url,
                    );
                    if (
                        Object.prototype.hasOwnProperty.call(
                            options.params,
                            "$filter",
                        )
                    ) {
                        options.params.$filter =
                            DataSourceService.convertFilterDatetimeToTimeOffset(
                                options.params.$filter,
                            );
                    }
                } else if (["PATCH", "POST"].includes(options.method ?? "")) {
                    /*
                    Our OData API endpoint does not support inserts/updates.
                    Perform updates from events in the UI component
                    instead, eg DataGrid's onSaving event.

                    We still need to do some API call here, otherwise the UI
                    will show some error. So just call userheader endpoint.
                    After this call, the DataGrid will reload the store to
                    fetch the new changes.
                    */
                    options.method = "GET";
                    options.url = `${basePath}/myaccount/info`;
                }
            },
            version: 4,
        };
        const mergedDataStoreOptions: ODataStoreOptions = {
            ...defaultDataStoreOptions,
            ...dataStoreOptions,
        };

        const dataStore: ODataStore = new ODataStore(mergedDataStoreOptions);

        const defaultDataSourceOptions: DataSourceOptions = {
            store: dataStore,
            select: select,
        };
        const mergedDataSourceOptions: DataSourceOptions = {
            ...defaultDataSourceOptions,
            ...dataSourceOptions,
        };
        return new DataSource(mergedDataSourceOptions);
    }

    public dnspOperatingEnvelopeDataSource() {
        return new DataSource([]);
    }

    public dnspLiveStatsDataSource() {
        return new DataSource([]);
    }

    public dnspDevicesInfoDataSource() {
        return new DataSource([]);
    }

    public dnspDrEventStatsDataSource(
        projectId: string,
        dnspId: string,
    ): DataSource {
        const key = `${projectId}_${dnspId}`;

        if (this._dnspDrEventStatsDataSources[key]) {
            return this._dnspDrEventStatsDataSources[key];
        }

        const store: CustomStore = new CustomStore({
            key: "drEventId",
            loadMode: "raw",
            cacheRawData: true,
            load: async (loadOptions: LoadOptions) => {
                const api = await createDnspDashboardApiAsync(
                    this._errorContext,
                    this._msalInstance,
                );
                const response = await api.dnspGetDerEvents({
                    limit: 10000,
                    includeStatistics: true,
                    startingBefore: new Date(), // Only get historic DR events.
                    dnspTag: dnspId,
                });
                const data: IDrEventStatistics[] = response.map((i) => {
                    const viewModel: IDrEventStatistics = {
                        drEventId: i.derEventId || "",
                        dnsp: i.dnspName || "",
                        kWhSaved: i.powerSavedWh || 0,
                        participants: i.participantCount || 0,
                        chargers: i.deviceCount || 0,
                        start: i.startUtc || new Date(),
                        end: i.endUtc || new Date(),
                    };

                    return viewModel;
                });

                return DataSourceService.applyLoadOptions(loadOptions, data);
            },
        });

        this._dnspDrEventStatsDataSources[key] =
            DataSourceService.createDataSource(store, 5, true);

        return this._dnspDrEventStatsDataSources[key];
    }

    /**
     * Create a datasource for the live power consumption of a device (Only CORE and power meters are supported).
     * Retrieves the last 24 hours of data and then streams live updates.
     * @returns A promise that resolves with the power consumption data source.
     */
    public async powerConsumtionDataSource(
        deviceId: string,
    ): Promise<PowerConsumptionDataSource> {
        const fetchAync = async (
            from: string,
            to: string,
        ): Promise<PowerConsumptionResponse[]> => {
            const response = await api.getPowerConsumption({
                from: from,
                to: to,
                deviceId: deviceId,
                interval: GetChargeSessionCountIntervalEnum.Minute, // This is actually raw/unsampled and not per minute.
                inLocalTime: false,
            });

            if (!response) {
                throw new Error("The resonse is undefined.");
            }

            return response;
        };

        const api =
            await ApiService.createConsumptionDashboardHttpFunctionsApiAsync(
                this._errorContext,
                this._msalInstance,
            );
        const customStore: CustomStore<PowerConsumptionResponse, string> =
            new CustomStore<PowerConsumptionResponse, string>({
                cacheRawData: true,
                loadMode: "raw",
                load: async () => {
                    const now: Date = new Date();
                    const to: string = new Date().toUTCString();
                    const from: string = new Date(
                        now.setHours(now.getHours() - 24),
                    ).toUTCString();

                    dataSource.response = await fetchAync(from, to);

                    return dataSource.response ?? [];
                },
                key: "label",
            });
        const dataSource: PowerConsumptionDataSource = new DataSource<
            PowerConsumption,
            string
        >({
            store: customStore,
            paginate: false,
        });

        // We dont know the interval yet based on existing data and setting the interval in the onload causes a race condition.
        // We use the 30 second interval which is the current raw/unsampled rate.
        // 80/20 rule, no more time on this, it is good enough.
        const inverval: number = 30 * 1000;

        dataSource.timer = setInterval(async () => {
            const dataSourceItems: PowerConsumption[] = dataSource.items();
            const lastItem: PowerConsumption | undefined =
                dataSourceItems.length > 0
                    ? dataSourceItems[dataSourceItems.length - 1]
                    : undefined;
            const now: Date = new Date();
            const to: string = now.toUTCString();
            const from: string =
                lastItem?.timestamp?.toUTCString() ||
                new Date(now.setMinutes(now.getMinutes() - 2)).toUTCString();
            const response: PowerConsumptionResponse[] = await fetchAync(
                from,
                to,
            );

            if (response.length > 0) {
                customStore.push(
                    response.map((item: PowerConsumptionResponse) => {
                        return {
                            type: "insert",
                            data: item,
                            key: item.deviceIdentity,
                        };
                    }),
                );
            }
        }, inverval);

        return dataSource;
    }

    // Hacky -- gets auth header non-async but it will be correct in the next call
    private async getAuthHeaderAsync() {
        this._authHeader = await AuthService.getAuthorizationHeader(
            this._msalInstance,
        );
        return this._authHeader;
    }

    /**
     * Wait for a task to result (either Complete or Failed), by listening to SignalR msg.
     * Promise rejects if times out.
     * @param taskInfoId
     * @returns The resulted Task if Complete or Failed. (Promise rejects if times out.)
     */
    public waitForTaskAsync(
        taskInfoId: string,
        timeoutMs: number = 60 * 1000,
    ): Promise<TaskInfoUpdateMessage> {
        return new Promise<TaskInfoUpdateMessage>((resolve, reject) => {
            if (!this.signalRHub)
                return reject(
                    new Error(
                        "SignalR not initialised, unable to listen to response from Device Manager.",
                    ),
                );
            const lsignalRhub = this.signalRHub;
            lsignalRhub.on("taskInfoUpdateMessage", onTaskInfoUpdateMessage);
            function onTaskInfoUpdateMessage(message: TaskInfoUpdateMessage) {
                console.log(
                    `data-source onTaskInfoUpdateMessage for ${message.taskInfoId} stage: ${message.stage}`,
                );
                if (message.taskInfoId === taskInfoId) {
                    //Promise resolve()s after the first Complete or Failed update.
                    //Component stops listening, won't handle a Failed then a Complete msg.
                    const stage = convertTaskStageDtoToTaskStageViewModel(
                        message.stage,
                    );
                    if (
                        stage === TaskStageViewModel.Complete ||
                        stage === TaskStageViewModel.Failed
                    ) {
                        return resolve(message);
                    }
                }
            }

            new Promise((r) => setTimeout(r, timeoutMs)).then(() => {
                //This will always execute, regardless if task already successfully completed or not.
                //That's ok, rejecting a promise that has already completed does nothing.
                console.log(
                    "Timed out waiting for response from Device Manager for task " +
                        taskInfoId,
                );
                lsignalRhub.off(
                    "taskInfoUpdateMessage",
                    onTaskInfoUpdateMessage,
                );
                reject(
                    new Error(
                        "Timed out waiting for response from Device Manager",
                    ),
                );
            });
        });
    }

    // Find and Fix ISO dates using regex
    private static convertFilterDatetimeToTimeOffset(filter: string): string {
        const regex = /'\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+Z'/g;
        const datetimes = filter.matchAll(regex);
        if (!datetimes) {
            return filter;
        }
        for (const dateString of Array.from(datetimes)) {
            const trimmedDateString = dateString[0].replaceAll("'", "");
            filter = filter.replace(dateString[0], trimmedDateString);
        }
        return filter;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static applyLoadOptions(loadOptions: LoadOptions, data: any[]) {
        if (loadOptions.sort) {
            const loadOptionsSort = loadOptions.sort;
            const sortOptions: ISortOptions = {
                selector: loadOptionsSort.toString(),
                desc: false,
            };

            data = data.sort((a, b) => {
                let comparisonResult: number;

                if (a[sortOptions.selector] > b[sortOptions.selector]) {
                    comparisonResult = !sortOptions.desc ? 1 : -1;
                } else if (a[sortOptions.selector] < b[sortOptions.selector]) {
                    comparisonResult = !sortOptions.desc ? -1 : 1;
                } else {
                    comparisonResult = 0;
                }

                return comparisonResult;
            });
        }

        if (loadOptions.skip) {
            data = drop(data, loadOptions.skip);
        }

        if (loadOptions.take) {
            data = take(data, loadOptions.take);
        }
        if (loadOptions.filter) {
            console.log("filter", loadOptions.filter);
        }

        return data;
    }

    private static createDataSource(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        store: any,
        pageSize?: number,
        paginate?: boolean,
    ): DataSource {
        const dataSourceOptions: DataSourceOptions = {
            store,
            pageSize,
            paginate: paginate,
        };
        const dataSource: DataSource = new DataSource(dataSourceOptions);

        return dataSource;
    }

    private static createPivotGridDataSource(
        store:
            | XmlaStore
            | Array<{ type?: "array" | "local" | "odata" | "xmla" }>
            | { type?: "array" | "local" | "odata" | "xmla" },
        fields: Array<Field>,
    ): PivotGridDataSource {
        const dataSourceOptions: Options = {
            fields,
            store,
        };
        const dataSource: PivotGridDataSource = new PivotGridDataSource(
            dataSourceOptions,
        );

        return dataSource;
    }

    /**
     * Replace a odata request like /items-odata(1) with /items-odata?$filter=itemId eq 1
     * Because our API does not support id-based urls, and also does not support POST or PATCH requests.
     * @param storeUrl
     * @param key
     * @param url
     * @returns
     */
    private static replaceODataIDLookup(
        storeUrl: string,
        key: string,
        url: string,
    ): string {
        const re = /\(([\w'-]+)\)/gm;
        const idMatchResult = re.exec(url);
        if (idMatchResult?.[1]) {
            const id = idMatchResult[1];
            const idWithBrackets = `(${id})`;
            const filterExpression = `$filter=${key} eq ${id}`;
            if (url.endsWith(idWithBrackets)) {
                return `${storeUrl}?${filterExpression}`;
            } else {
                const params = url.split(idWithBrackets)[1];
                return `${storeUrl}${params}&${filterExpression}`;
            }
        }
        return url;
    }
}

/**
 * @deprecated Use the generated OData sibling hooks from `@/api/odata/odata-hooks.gen`
 * together with `useODataQuery` from `@/hooks/use-odata-query` instead.
 * See `src/components/data-table/MIGRATION.md`.
 */
export default DataSourceService;
